import { useTranslation } from "react-i18next";
import { cn, Icon, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@nexus/ui";
import type { ScheduledJob } from "@/features/scheduled-jobs/api/scheduled-jobs.api";
import { getPublishedDisplay } from "@/features/events/lib/event-row-display";
import {
  getScheduledJobCadenceDisplay,
  getScheduledJobCadenceSummary,
  humanizeScheduledJobId,
  isScheduledJobOverdue,
  scheduledJobIdTooltip,
  type ScheduledJobCadenceSummary,
} from "../lib/scheduled-job-display";
import { ScheduledJobHealthBadge } from "./ScheduledJobHealthBadge";
import { ScheduledJobStateBadge } from "./ScheduledJobStateBadge";

interface ScheduledJobsTableProps {
  rows: ScheduledJob[];
  now: number;
  /** Slack past a job's next-run time before it flags overdue. From the ops settings. */
  overdueGraceMs?: number;
  /** Streak length at which the health badge turns red. From the ops settings. */
  unhealthyAfterFailures?: number;
  onSelectRow: (row: ScheduledJob) => void;
  selectedRowId: string | null;
}

const MONO_MICRO_LABEL = "font-mono text-[10px] uppercase tracking-wide text-muted-foreground";
const EMPTY_VALUE = "—";

export function ScheduledJobsTable({
  rows,
  now,
  overdueGraceMs,
  unhealthyAfterFailures,
  onSelectRow,
  selectedRowId,
}: ScheduledJobsTableProps) {
  const { t, i18n } = useTranslation();

  return (
    <Table className="table-fixed">
      <colgroup>
        <col />
        <col className="w-[220px]" />
        <col className="w-[112px]" />
        <col className="w-[150px]" />
        <col className="w-[150px]" />
        <col className="w-[140px]" />
        <col className="w-9" />
      </colgroup>
      <TableHeader className="sticky top-0 z-10">
        <TableRow className="hover:bg-muted">
          <TableHead>{t("admin.scheduledJobs.table.job", "Job")}</TableHead>
          <TableHead>{t("admin.scheduledJobs.table.cadence", "Cadence")}</TableHead>
          <TableHead>{t("admin.scheduledJobs.table.state", "State")}</TableHead>
          <TableHead>{t("admin.scheduledJobs.table.nextRun", "Next run")}</TableHead>
          <TableHead>{t("admin.scheduledJobs.table.lastRun", "Last run")}</TableHead>
          <TableHead>{t("admin.scheduledJobs.table.health", "Health")}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const nextRun = row.nextScheduleTime
            ? getPublishedDisplay(row.nextScheduleTime, i18n.language)
            : null;
          const lastRun = row.lastAttempt?.time
            ? getPublishedDisplay(row.lastAttempt.time, i18n.language)
            : null;
          const isOverdue = isScheduledJobOverdue(row, now, overdueGraceMs);
          const cadence = getScheduledJobCadenceSummary(row);
          const expression = getScheduledJobCadenceDisplay(row);
          // Whitespace-only is a description an operator never wrote; a blank
          // line in the cell would read as one they did.
          const description = row.description?.trim() ?? "";

          return (
            <TableRow
              key={row.name}
              onClick={() => onSelectRow(row)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectRow(row);
                }
              }}
              tabIndex={0}
              aria-label={t("admin.scheduledJobs.table.rowLabel", "Open scheduled job {{id}}", {
                id: scheduledJobIdTooltip(row.id),
              })}
              data-state={selectedRowId === row.name ? "selected" : undefined}
              className="cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
            >
              <TableCell className="min-w-0">
                <div className="truncate font-medium" title={scheduledJobIdTooltip(row.id)}>
                  {humanizeScheduledJobId(row.id)}
                </div>
                {description ? (
                  <div
                    className="truncate text-[11px] leading-4 text-muted-foreground"
                    title={description}
                  >
                    {description}
                  </div>
                ) : null}
                {row.eventType ? (
                  <div className={cn(MONO_MICRO_LABEL, "truncate")} title={row.eventType}>
                    {row.eventType}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {cadence ? <div className="truncate">{cadenceText(cadence, t)}</div> : null}
                <div
                  className={cn(
                    "truncate font-mono text-[11px]",
                    cadence && "text-muted-foreground",
                  )}
                  title={expression}
                >
                  {expression}
                </div>
              </TableCell>
              <TableCell>
                <ScheduledJobStateBadge state={row.state} />
              </TableCell>
              <TableCell
                className={cn("whitespace-nowrap tabular-nums", isOverdue && "text-warning")}
              >
                {nextRun ? (
                  <>
                    <div>{nextRun.time}</div>
                    <div className="text-[11px] text-muted-foreground">{nextRun.date}</div>
                  </>
                ) : (
                  EMPTY_VALUE
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap tabular-nums">
                {lastRun ? (
                  <>
                    <div
                      className={row.lastAttempt?.outcome === "ERROR" ? "text-error" : undefined}
                    >
                      {lastRun.time}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{lastRun.date}</div>
                  </>
                ) : (
                  EMPTY_VALUE
                )}
              </TableCell>
              <TableCell>
                <ScheduledJobHealthBadge
                  health={row.health}
                  isOverdue={isOverdue}
                  unhealthyAfterFailures={unhealthyAfterFailures}
                />
              </TableCell>
              <TableCell className="px-2 text-muted-foreground">
                <Icon name="chevron-right" size="xs" aria-hidden />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/**
 * Phrases, not the create form's sentences: a column of "Runs hourly at minute
 * 00." reads as prose, while eight rows of "Hourly at :00" compare at a glance.
 */
function cadenceText(
  cadence: ScheduledJobCadenceSummary,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string {
  const { params } = cadence;
  switch (cadence.key) {
    case "everyMinute":
      return t("admin.scheduledJobs.table.cadenceSummary.everyMinute", "Every minute");
    case "everyNMinutes":
      return t("admin.scheduledJobs.table.cadenceSummary.everyNMinutes", "Every {{n}} min", {
        n: params.n,
      });
    case "hourly":
      return t("admin.scheduledJobs.table.cadenceSummary.hourly", "Hourly at :{{minute}}", {
        minute: params.minute,
      });
    case "everyNHours":
      return t(
        "admin.scheduledJobs.table.cadenceSummary.everyNHours",
        "Every {{n}}h at :{{minute}}",
        { n: params.n, minute: params.minute },
      );
    case "dailyAt":
      return t("admin.scheduledJobs.table.cadenceSummary.dailyAt", "Daily at {{time}}", {
        time: params.time,
      });
    case "weekdaysAt":
      return t("admin.scheduledJobs.table.cadenceSummary.weekdaysAt", "Weekdays at {{time}}", {
        time: params.time,
      });
    case "weeklyAt":
      return t(
        "admin.scheduledJobs.table.cadenceSummary.weeklyAt",
        "Weekly on day {{day}} at {{time}}",
        { day: params.day, time: params.time },
      );
  }
}
